import React, { useEffect, useState } from "react";
import { db } from "../lib/firebase";
import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import { Search, Tag, X, Save, AlertCircle } from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import { getDiscountedPrice, formatPrice } from "../utils/priceUtils";

const AdminDiscounts = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");

    // Modal State
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [discountType, setDiscountType] = useState("percent");
    const [discountValue, setDiscountValue] = useState(0);

    // Fetch Products
    const fetchProducts = async () => {
        setLoading(true);
        try {
            const snapshot = await getDocs(collection(db, "products"));
            const data = snapshot.docs.map((docItem) => ({
                id: docItem.id,
                ...docItem.data(),
            }));
            setProducts(data);
        } catch (error) {
            console.error("Failed to fetch products", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProducts();
    }, []);

    // Safe Normalization Helper
    const norm = (v) => String(v ?? "").toLowerCase().trim();

    // Safe Products Array
    const safeProducts = Array.isArray(products) ? products : [];

    // Filtered Products
    const filteredProducts = safeProducts.filter(p => {
        if (!p) return false;
        const term = norm(search);
        const name = norm(p.name);
        const category = norm(p.category);
        return name.includes(term) || category.includes(term);
    });

    // Open Modal
    const handleOpenModal = (product) => {
        setSelectedProduct(product);
        if (product.discount && product.discount.isActive) {
            setDiscountType(product.discount.type || "percent");
            setDiscountValue(product.discount.value || 0);
        } else {
            setDiscountType("percent");
            setDiscountValue(0);
        }
    };

    // Close Modal
    const handleCloseModal = () => {
        setSelectedProduct(null);
        setDiscountValue(0);
    };

    // Save Discount
    const handleSave = async () => {
        if (!selectedProduct) return;

        // Validation
        const val = Number(discountValue);
        if (val < 0) return toast.error("Değer 0'dan küçük olamaz.");
        if (discountType === 'percent' && val > 99) return toast.error("Yüzde 99'dan büyük olamaz.");
        if (discountType === 'amount' && val >= Number(selectedProduct.price)) return toast.error("İndirim fiyatı ürün fiyatından büyük olamaz.");

        try {
            const ref = doc(db, "products", selectedProduct.id);
            const discountData = {
                isActive: true,
                type: discountType,
                value: val,
                updatedAt: new Date().toISOString()
            };

            await updateDoc(ref, { discount: discountData });

            toast.success("İndirim uygulandı!");
            fetchProducts(); // Refresh list
            handleCloseModal();
        } catch (error) {
            console.error("Save error", error);
            toast.error("Hata oluştu.");
        }
    };

    // Remove Discount
    const handleRemove = async (e, productId) => {
        e.stopPropagation();
        if (!window.confirm("İndirimi kaldırmak istediğinize emin misiniz?")) return;

        try {
            const ref = doc(db, "products", productId);
            await updateDoc(ref, { "discount.isActive": false });
            toast.info("İndirim kaldırıldı.");
            fetchProducts();
        } catch (error) {
            console.error("Remove error", error);
            toast.error("Hata oluştu.");
        }
    };

    // Preview Calculation
    const previewPrice = selectedProduct ? getDiscountedPrice({
        ...selectedProduct,
        discount: { isActive: true, type: discountType, value: Number(discountValue) }
    }) : 0;

    return (
        <div className="p-8">
            <ToastContainer />
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800">İndirim Yönetimi</h1>
                    <p className="text-gray-500 mt-1">Ürünlere kampanya ve indirim uygulayın</p>
                </div>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                        type="text"
                        placeholder="Ürün Ara..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none w-64"
                    />
                </div>
            </div>

            {loading ? (
                <div className="text-center py-20 text-gray-500">Yükleniyor...</div>
            ) : (
                <div className="bg-white rounded-xl shadow overflow-hidden">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 border-b">
                            <tr>
                                <th className="p-4 font-semibold text-gray-600">Ürün</th>
                                <th className="p-4 font-semibold text-gray-600">Fiyat</th>
                                <th className="p-4 font-semibold text-gray-600">İndirim Durumu</th>
                                <th className="p-4 font-semibold text-gray-600">Son Fiyat</th>
                                <th className="p-4 font-semibold text-gray-600 text-right">İşlem</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {filteredProducts.map(product => {
                                const hasDiscount = product.discount && product.discount.isActive;
                                const final = getDiscountedPrice(product);

                                return (
                                    <tr key={product.id} className="hover:bg-gray-50 transition-colors group">
                                        <td className="p-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-12 h-12 bg-gray-100 rounded-lg p-1">
                                                    <img src={product.image} alt="" className="w-full h-full object-contain mix-blend-multiply" />
                                                </div>
                                                <div>
                                                    <h4 className="font-semibold text-gray-800">{product.name}</h4>
                                                    <span className="text-xs text-gray-500 uppercase">{product.category}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-4 font-mono text-gray-600">{formatPrice(product.price)}</td>
                                        <td className="p-4">
                                            {hasDiscount ? (
                                                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-bold border border-red-200">
                                                    <Tag className="w-3 h-3" />
                                                    {product.discount.type === 'percent' ? `-%${product.discount.value}` : `-${product.discount.value}₺`}
                                                </span>
                                            ) : (
                                                <span className="text-sm text-gray-400">-</span>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            <span className={`font-bold ${hasDiscount ? 'text-red-600' : 'text-gray-800'}`}>
                                                {formatPrice(final)}
                                            </span>
                                        </td>
                                        <td className="p-4 text-right">
                                            {hasDiscount && (
                                                <button
                                                    onClick={(e) => handleRemove(e, product.id)}
                                                    className="px-3 py-1 text-sm text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg font-medium mr-2"
                                                >
                                                    Kaldır
                                                </button>
                                            )}
                                            <button
                                                onClick={() => handleOpenModal(product)}
                                                className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-black transition-colors shadow-sm"
                                            >
                                                {hasDiscount ? 'Düzenle' : 'İndirim Uygula'}
                                            </button>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* MODAL */}
            {selectedProduct && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className="p-6 border-b flex justify-between items-center bg-gray-50">
                            <h3 className="font-bold text-xl text-gray-800">İndirim Ayarla</h3>
                            <button onClick={handleCloseModal} className="p-2 hover:bg-gray-200 rounded-full text-gray-500">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6 space-y-6">
                            <div className="flex items-center gap-4 bg-blue-50 p-4 rounded-xl border border-blue-100">
                                <img src={selectedProduct.image} className="w-16 h-16 object-contain mix-blend-multiply bg-white rounded-lg p-1" alt="" />
                                <div>
                                    <h4 className="font-bold text-gray-900">{selectedProduct.name}</h4>
                                    <p className="text-gray-500 text-sm">Orjinal Fiyat: <span className="font-mono font-bold text-gray-800">{formatPrice(selectedProduct.price)}</span></p>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">İndirim Tipi</label>
                                    <div className="grid grid-cols-2 gap-3">
                                        <button
                                            onClick={() => setDiscountType('percent')}
                                            className={`py-2 px-4 rounded-lg border text-sm font-semibold transition-all ${discountType === 'percent' ? 'bg-black text-white border-black ring-2 ring-black/20' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                                        >
                                            Yüzde (%)
                                        </button>
                                        <button
                                            onClick={() => setDiscountType('amount')}
                                            className={`py-2 px-4 rounded-lg border text-sm font-semibold transition-all ${discountType === 'amount' ? 'bg-black text-white border-black ring-2 ring-black/20' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                                        >
                                            Tutar (₺)
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {discountType === 'percent' ? 'İndirim Yüzdesi (%)' : 'İndirim Tutarı (₺)'}
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            value={discountValue}
                                            onChange={(e) => setDiscountValue(e.target.value)}
                                            className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-black/10 focus:border-black outline-none text-lg font-bold"
                                            placeholder="0"
                                            min="0"
                                            max={discountType === 'percent' ? "100" : undefined}
                                        />
                                        <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">
                                            {discountType === 'percent' ? '%' : '₺'}
                                        </div>
                                    </div>
                                </div>

                                <div className="pt-4 border-t flex justify-between items-center">
                                    <span className="text-sm font-medium text-gray-500">Yeni Fiyat Önizleme:</span>
                                    <span className="text-2xl font-black text-green-600">{formatPrice(previewPrice)}</span>
                                </div>
                            </div>
                        </div>

                        <div className="p-6 bg-gray-50 border-t flex justify-end gap-3">
                            <button onClick={handleCloseModal} className="px-5 py-2.5 text-gray-600 font-bold hover:bg-gray-200 rounded-lg transition-colors">
                                İptal
                            </button>
                            <button onClick={handleSave} className="px-5 py-2.5 bg-black text-white font-bold rounded-lg hover:bg-gray-800 shadow-lg flex items-center gap-2">
                                <Save className="w-4 h-4" />
                                Kaydet
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminDiscounts;
